import type { FileTemplate } from "schemas/generated/fileTemplate";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useInfiniteFileTemplates } from "@editor/hooks/useInfiniteFileTemplates";
import { useModal } from "@editor/hooks/useModal";

import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { FolderSearch } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";

import PageTemplateCard, { PageTemplateCardSkeleton } from "./PageTemplateCard";

const TEMPLATE_GRID_CONSTANTS = {
  MIN_CARD_WIDTH: 300,
} as const;

function PageTemplatesSection() {
  const projectId = useCurrentProjectId();
  const { openModal } = useModal();

  // Try to load project templates first
  const {
    templates: projectTemplates,
    isLoading: isLoadingProjectTemplates,
    fetchNextPage: fetchNextProjectPage,
    hasNextPage: hasNextProjectPage,
  } = useInfiniteFileTemplates({
    projectId: projectId ?? null,
    type: "page",
    limit: 8,
    onlyGlobalTemplates: false,
  });

  // Fallback to global templates if no project templates exist
  const {
    templates: globalTemplates,
    isLoading: isLoadingGlobalTemplates,
    fetchNextPage: fetchNextGlobalPage,
    hasNextPage: hasNextGlobalPage,
  } = useInfiniteFileTemplates({
    projectId: projectId ?? null,
    type: "page",
    limit: 8,
    onlyGlobalTemplates: true,
    enabled: projectTemplates.length === 0 && !isLoadingProjectTemplates,
  });

  const handleTemplateClick = (template: FileTemplate) => {
    openModal({
      type: "pageTemplatePreviewModal",
      props: {
        template,
        chatId: "",
      },
    });
  };

  // Determine which templates to display and which pagination to use
  const displayedTemplates =
    projectTemplates.length > 0 ? projectTemplates : globalTemplates;
  const isLoading = isLoadingProjectTemplates || isLoadingGlobalTemplates;
  const fetchNextPage =
    projectTemplates.length > 0 ? fetchNextProjectPage : fetchNextGlobalPage;
  const hasNextPage =
    projectTemplates.length > 0 ? hasNextProjectPage : hasNextGlobalPage;

  return (
    <div className="flex flex-col h-full">
      {displayedTemplates?.length === 0 && !isLoading ? (
        <EmptyState
          title="No templates found"
          description="Start building with Replo — we can't wait to bring your ideas to life!"
          icon={<FolderSearch className="icon-size-default" />}
          UNSAFE_className="py-[10%]"
        />
      ) : (
        <InfiniteScroll
          dataLength={displayedTemplates.length}
          next={() => void fetchNextPage()}
          hasMore={hasNextPage}
          loader={
            <div
              className="grid gap-4 mt-4 grid-cols-1"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${TEMPLATE_GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
              }}
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <PageTemplateCardSkeleton key={`loader-${index}`} />
              ))}
            </div>
          }
          scrollableTarget="home-scroll-container"
        >
          <div
            className="grid gap-4 pb-6 grid-cols-1"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${TEMPLATE_GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
            }}
          >
            {isLoading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <PageTemplateCardSkeleton key={`initial-${index}`} />
                ))
              : displayedTemplates?.map((template) => (
                  <PageTemplateCard
                    key={template.id}
                    template={template}
                    onClick={() => handleTemplateClick(template)}
                  />
                ))}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
}

export default PageTemplatesSection;
